import { MouseEvent, Ref, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationOn, South, HourglassEmpty, RouteOutlined, ExpandMore, EventNote, Lock } from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Card,
  CardActionArea,
  CardContent,
  FormControlLabel,
  Typography,
} from '@mui/material';

import { DetailedRoute } from '@/generated/model/detailedRoute';
import { time, timeDurationShort } from '@/utilities/timeUtils';
import { formatDistanceKm } from '@/utilities/distanceUtils';
import { ctxPluto } from '@/contexts/PlutoContext';
import { RouteEventDetail } from './RouteEventDetail';
import { numberZeroOrBlank } from '@/utilities/preconditions';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';
import FlagsWithGeoAdress from '../common/FlagsWithGeoAddr';

import './RouteStyle.css';

export function RouteDetail({
  route,
  className,
  ref,
}: {
  route: DetailedRoute | undefined;
  className: string;
  ref: Ref<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const ctx = useContext(ctxPluto);
  const ctxRoute = useContext(ctxRoutes);
  const [openEvents, setOpenEvents] = useState<boolean>(false);
  const [isHighlighted, setHightlighted] = useState<boolean>(
    route && ctxRoute?.highlightedRoutes ? ctxRoute.highlightedRoutes.includes(route.id) : false
  );

  useEffect(() => {
    if (!route) return;
    if (!ctxRoute?.highlightedRoutes?.includes(route.id)) {
      setHightlighted(false);
    }
  }, [ctxRoute?.highlightedRoutes, route]);

  if (!ctx) return;

  if (!route) {
    return (
      <div className="route-empty-div" data-qa-element-id="no_route_label">
        {t('routes.emptyDay')}
      </div>
    );
  }

  function toggleAndSortHighlightedRoutes({
    currentHighlighted,
    clickedRouteId,
    allRoutes,
  }: {
    currentHighlighted: number[] | undefined;
    clickedRouteId: number;
    allRoutes: number[] | undefined;
  }) {
    let updated: number[];
    if (!allRoutes || !currentHighlighted) return undefined;

    if (currentHighlighted.includes(clickedRouteId)) {
      updated = currentHighlighted.filter((id) => id !== clickedRouteId);
    } else {
      updated = [...currentHighlighted, clickedRouteId];
    }

    return updated.sort(
      (a, b) => allRoutes.findIndex((route) => route === a) - allRoutes.findIndex((route) => route === b)
    );
  }

  return (
    <Card className={`route-detail ${className}`} ref={ref} data-qa-element-id="route_panel">
      <CardActionArea
        disableRipple
        className={isHighlighted ? 'route-detail-highlighted' : undefined}
        onClick={() => {
          ctxRoute?.setHighlightedRoutes((prev) =>
            toggleAndSortHighlightedRoutes({
              currentHighlighted: prev,
              clickedRouteId: route.id,
              allRoutes: ctxRoute.routes?.map((r) => r.id),
            })
          );

          setHightlighted(!isHighlighted);
        }}
      >
        <CardContent className="route-detail-content">
          <Typography variant="h6" component="div" data-qa-element-id="route_header_panel">
            <div className="route-detail-header">
              <div className="route-detail-header-row">
                {route.driver ? (
                  <div className="route-driver-name" data-qa-element-id="route_driver_header">
                    <img className="route-driver-icon" src="svg/steeringwheel.svg" />
                    <div className="route-driver-name-text" data-qa-element-id="driver_name_label">
                      {route.driver.name}
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </div>
            </div>
          </Typography>
          <Typography variant="body2" component="div">
            <div className="route-location">
              <span className="route-time-tag" data-qa-element-id="route_start_time">
                {time(route.startPosition.timeMicros)}
              </span>
              <span data-qa-element-id="route_start_type_icon">
                {route.privateRoute ? (
                  <Lock className="route-location-icon" />
                ) : (
                  <LocationOn className="route-location-icon" />
                )}
              </span>
              <div className="route-location-address" data-qa-element-id="route_start_address">
                {route.privateRoute && !route.startPosition.geocodedAddress ? (
                  t('routes.privateRoute')
                ) : (
                  <FlagsWithGeoAdress
                    countryCode={route.startPosition.countryCode}
                    countryName={route.startPosition.countryName}
                    geocodedAddress={
                      ctxRoute?.poiNamesEnabled && route.startPosition.poi
                        ? route.startPosition.poi
                        : route.startPosition.geocodedAddress
                    }
                  />
                )}
              </div>
            </div>

            <div className="route-distance-details">
              <South className="route-details-type-icon" data-qa-element-id="arrow_down_icon" />
              <div className="route-distance-detail-information">
                <span data-qa-element-id="route_duration">{timeDurationShort(t, route.durationTimeMicros)}</span>
                <span className="route-text-separator-char" data-qa-element-id="duration_length_separator_icon">
                  <RouteOutlined fontSize="small" color="secondary" />
                </span>
                <span data-qa-element-id="route_length">{formatDistanceKm(route.distanceKm)}</span>
              </div>
            </div>

            {route.stopPosition && (
              <div className="route-location">
                <span className="route-time-tag route-time-tag-destination" data-qa-element-id="route_end_time">
                  {time(route.stopPosition.timeMicros)}
                </span>
                <span data-qa-element-id="route_end_type_icon">
                  {route.privateRoute ? (
                    <Lock className="route-location-icon" />
                  ) : (
                    <LocationOn className="route-location-icon" />
                  )}
                </span>
                <div className="route-location-address" data-qa-element-id="route_end_address">
                  {route.privateRoute && !route.stopPosition.geocodedAddress ? (
                    t('routes.privateRoute')
                  ) : (
                    <FlagsWithGeoAdress
                      countryCode={route.stopPosition.countryCode}
                      countryName={route.stopPosition.countryName}
                      geocodedAddress={
                        ctxRoute?.poiNamesEnabled && route.stopPosition.poi
                          ? route.stopPosition.poi
                          : route.stopPosition.geocodedAddress
                      }
                    />
                  )}
                </div>
              </div>
            )}

            <div className="route-distance-details">
              <HourglassEmpty className="route-details-type-icon" data-qa-element-id="hourglass_icon" />
              <div className="route-distance-detail-information" data-qa-element-id="stop_time_between_routes">
                {numberZeroOrBlank(route.standTimeMicros)
                  ? t('general.data.outOfInterval')
                  : timeDurationShort(t, route.standTimeMicros)}
              </div>
            </div>
            {/* onClick stopPropagation: enelkul kijeloli az utvonalat */}
            {route.events && route.events.length > 0 && (
              <Accordion
                onChange={() => setOpenEvents(!openEvents)}
                onClick={(e: MouseEvent) => e.stopPropagation()}
                sx={{
                  borderRadius: 'var(--mui-shape-borderRadius) !important',
                  margin: '0px !important',
                  '&::before': { display: 'none' },
                }}
                data-qa-element-id="route_events_dropdown"
              >
                <AccordionSummary
                  component="div"
                  expandIcon={<ExpandMore />}
                  sx={{
                    minHeight: '40px',
                    '&.Mui-expanded': {
                      minHeight: '40px',
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '8px 0',
                    },
                    '& .MuiAccordionSummary-content.Mui-expanded': {
                      margin: '8px 0',
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Badge
                        className="route-event-control"
                        color="primary"
                        badgeContent={route.events?.length || 0}
                        sx={{ '& .MuiBadge-badge': { right: -2, top: 12, border: `2px solid white` } }}
                        slotProps={{
                          badge: {
                            id: 'event_count_label',
                          },
                        }}
                      >
                        <EventNote />
                      </Badge>
                    }
                    label={t('routes.events')}
                    disabled={route.events ? route.events.length === 0 : true}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }} data-qa-element-id="route_events_detail_panel">
                  <RouteEventDetail events={route.events} />
                </AccordionDetails>
              </Accordion>
            )}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
